/**
 * Catchball Virtual Participant Config Store
 *
 * Zustand store for catchball_virtual_configs.
 * Keyed by cycleId. DB-first writes; no localStorage.
 *
 * §2.1 — durable state in Supabase.
 * §2.3 — schema (migration 378) + write path (saveVirtualConfig) +
 *          read path (fetchForCycle) + UI (CatchballSetupPage).
 */

import { create } from 'zustand';
import { getVirtualConfig, saveVirtualConfig, type SaveVirtualConfigInput } from '../../lib/catchballVirtualConfigService';
import type { CatchballVirtualConfig, CatchballParticipantRef, CatchballSetupMode } from '../../types/prioritization/catchballVirtualConfig';

// ── Store State ────────────────────────────────────────────────────────────

interface CatchballVirtualConfigState {
  /** Configs keyed by cycleId. */
  configs: Record<string, CatchballVirtualConfig>;
  /** Loading state per cycleId. */
  loading: Record<string, boolean>;
  /** Error message per cycleId. */
  errors: Record<string, string | null>;

  // ── Actions ──────────────────────────────────────────────────────────────

  /** Fetch (or return cached) virtual config for a cycle. */
  fetchForCycle: (cycleId: string) => Promise<CatchballVirtualConfig | null>;

  /**
   * Save the virtual config for a cycle.
   * Creates a new row or upserts the existing one.
   */
  save: (input: SaveVirtualConfigInput) => Promise<CatchballVirtualConfig | null>;

  /** Optimistically update the local config record. */
  setLocal: (cycleId: string, config: CatchballVirtualConfig) => void;

  /** Returns the setup mode for a cycle, defaulting to 'org_chart'. */
  getSetupMode: (cycleId: string) => CatchballSetupMode;

  /** Returns ball recipients for a cycle (empty array if org_chart mode or not loaded). */
  getBallRecipients: (cycleId: string) => CatchballParticipantRef[];

  /** Returns default participants for a cycle (empty array if org_chart mode or not loaded). */
  getDefaultParticipants: (cycleId: string) => CatchballParticipantRef[];
}

// ── Store ──────────────────────────────────────────────────────────────────

export const useCatchballVirtualConfigStore = create<CatchballVirtualConfigState>((set, get) => ({
  configs: {},
  loading: {},
  errors: {},

  fetchForCycle: async (cycleId) => {
    if (get().loading[cycleId]) return get().configs[cycleId] ?? null;

    set((s) => ({ loading: { ...s.loading, [cycleId]: true }, errors: { ...s.errors, [cycleId]: null } }));

    try {
      const config = await getVirtualConfig(cycleId);
      if (config) {
        set((s) => ({ configs: { ...s.configs, [cycleId]: config } }));
      }
      return config;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      set((s) => ({ errors: { ...s.errors, [cycleId]: message } }));
      return null;
    } finally {
      set((s) => ({ loading: { ...s.loading, [cycleId]: false } }));
    }
  },

  save: async (input) => {
    const { cycleId } = input;
    set((s) => ({ loading: { ...s.loading, [cycleId]: true }, errors: { ...s.errors, [cycleId]: null } }));

    try {
      const saved = await saveVirtualConfig(input);
      if (saved) {
        set((s) => ({ configs: { ...s.configs, [cycleId]: saved } }));
      }
      return saved;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      set((s) => ({ errors: { ...s.errors, [cycleId]: message } }));
      return null;
    } finally {
      set((s) => ({ loading: { ...s.loading, [cycleId]: false } }));
    }
  },

  setLocal: (cycleId, config) => {
    set((s) => ({ configs: { ...s.configs, [cycleId]: config } }));
  },

  getSetupMode: (cycleId) => {
    return get().configs[cycleId]?.setupMode ?? 'org_chart';
  },

  getBallRecipients: (cycleId) => {
    const config = get().configs[cycleId];
    if (!config || config.setupMode !== 'virtual') return [];
    return config.ballRecipients;
  },

  getDefaultParticipants: (cycleId) => {
    const config = get().configs[cycleId];
    if (!config || config.setupMode !== 'virtual') return [];
    return config.defaultParticipants;
  },
}));
